import { useEffect, useState, useRef } from "react";
import { api, BRL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LayoutGrid, List, Clock, Package, RefreshCw, Loader2, Printer } from "lucide-react";
import OpportunityDetail from "@/components/OpportunityDetail";
import PrintOptionsModal from "@/components/PrintOptionsModal";
import { useKanbanTouchDrag, DragGhost } from "@/lib/useKanbanTouchDrag";

const fmt = (iso) => { if (!iso) return "—"; const d = new Date(iso); return d.toLocaleDateString("pt-BR"); };

export default function Producao() {
  const [stages, setStages] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [items, setItems] = useState([]);
  const [dragged, setDragged] = useState(null);
  const [view, setView] = useState("kanban");
  const [detailItem, setDetailItem] = useState(null);
  const [printItem, setPrintItem] = useState(null);
  const scrollRef = useRef(null);
  const dragRef = useRef({ down: false, x: 0, left: 0 });
  const onDown = (e) => { if (e.target.closest('[draggable="true"]')) return; dragRef.current = { down: true, x: e.pageX, left: scrollRef.current.scrollLeft }; };
  const onMove = (e) => { if (!dragRef.current.down) return; e.preventDefault(); scrollRef.current.scrollLeft = dragRef.current.left - (e.pageX - dragRef.current.x); };
  const onUp = () => { dragRef.current.down = false; };

  const load = async () => {
    const [s, p, ps] = await Promise.all([api.get("/production/stages"), api.get("/production"), api.get("/pipeline/stages")]);
    setStages(s.data); setItems(p.data); setPipelineStages(ps.data);
  };
  useEffect(() => { load(); }, []);

  const [importing, setImporting] = useState(false);
  const importClosed = async () => {
    setImporting(true);
    try { const { data } = await api.post("/production/import-closed"); toast.success(`${data.imported} pedido(s) fechados importados`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao importar"); }
    finally { setImporting(false); }
  };
  const setDue = async (id, date) => {
    const { data } = await api.patch(`/production/${id}`, { due_date: date });
    if (data?.needs_reprint_alert) {
      toast.warning("Este pedido foi alterado. Lembre-se de reimprimir.", {
        action: { label: "Imprimir agora", onClick: () => setPrintItem(data) },
        duration: 6000,
      });
    }
    load();
  };
  const gridSummary = (it) => (it.size_grid || []).map((r) => `${r.qty}× ${r.modelagem} ${r.tamanho}`).join(" · ");
  const dueInfo = (it) => {
    if (!it.due_date || it.stage === "entregue") return null;
    const days = Math.ceil((new Date(it.due_date + "T00:00:00") - new Date()) / 86400000);
    if (days < 0) return { cls: "text-red-400 border-red-500/40", label: `Atrasado ${Math.abs(days)}d` };
    if (days <= 3) return { cls: "text-amber-400 border-amber-500/40", label: `Faltam ${days}d` };
    return { cls: "text-slate-400 border-slate-700", label: `Faltam ${days}d` };
  };

  const move = async (item, stage_key) => {
    if (item.stage === stage_key) return;
    if (!item.due_date) { toast.error("Defina o prazo de entrega antes de avançar a produção."); return; }
    const { data } = await api.patch(`/production/${item.id}/stage`, { stage_key });
    if (data?.needs_reprint_alert) {
      toast.warning("Este pedido foi alterado. Lembre-se de reimprimir.", {
        action: { label: "Imprimir agora", onClick: () => setPrintItem(data) },
        duration: 6000,
      });
    }
    load();
  };
  const { drag, start: touchStart } = useKanbanTouchDrag(move);
  const stageName = (k) => stages.find((s) => s.key === k)?.name || k;
  const totalPieces = (it) => (it.size_grid?.length ? it.size_grid.reduce((a, b) => a + (Number(b.qty) || 0), 0) : (it.quantity || 0));
  const todayISO = new Date().toISOString().slice(0, 10);

  const printInfo = (it) => {
    const printed = !!it.last_printed_at;
    const dirty = it.print_dirty !== false; // default treat missing as dirty
    const isRed = !printed || dirty;
    let tip;
    if (!printed) tip = "Nunca foi impresso";
    else if (dirty) tip = "Alterado após a última impressão — reimprimir";
    else tip = `Impresso em ${new Date(it.last_printed_at).toLocaleString("pt-BR")}${it.last_printed_by_name ? ` por ${it.last_printed_by_name}` : ""}`;
    return { isRed, tip };
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-between shrink-0 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Produção</h1>
          <p className="text-slate-400 text-sm mt-1">Pedidos fechados entram automaticamente em "Não Iniciado"</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={importClosed} disabled={importing} variant="outline" className="border-[#0CDBF2]/40 text-[#0CDBF2] hover:bg-[#0CDBF2]/10" data-testid="prod-import-closed">
            {importing ? <Loader2 size={14} className="animate-spin mr-2"/> : <RefreshCw size={14} className="mr-2"/>}Importar fechados
          </Button>
          <div className="flex rounded-lg border border-[#1E293B] bg-[#0B0F17] p-1" data-testid="prod-view-toggle">
            <button onClick={() => setView("kanban")} data-testid="prod-view-kanban"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view==="kanban" ? "bg-[#0CDBF2] text-[#07090E]" : "text-slate-400 hover:text-white"}`}>
              <LayoutGrid size={14}/> Kanban
            </button>
            <button onClick={() => setView("list")} data-testid="prod-view-list"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view==="list" ? "bg-[#0CDBF2] text-[#07090E]" : "text-slate-400 hover:text-white"}`}>
              <List size={14}/> Lista
            </button>
          </div>
        </div>
      </div>

      {view === "kanban" ? (
        <div ref={scrollRef} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
          className="flex gap-4 overflow-x-auto px-4 sm:px-6 pb-6 flex-1 min-h-0 items-start cursor-grab active:cursor-grabbing select-none snap-x snap-mandatory md:snap-none">
          {stages.map((s) => {
            const list = items.filter((i) => i.stage === s.key);
            return (
              <div key={s.key} className={`kanban-col flex-shrink-0 snap-start w-72 bg-[#0B0F17] border rounded-xl flex flex-col ${drag?.over===s.key ? "border-[#0CDBF2] ring-2 ring-[#0CDBF2]/60" : "border-[#1E293B]"}`}
                onDragOver={(e) => e.preventDefault()} onDrop={() => dragged && move(dragged, s.key)} data-col-key={s.key} data-testid={`prod-col-${s.key}`}>
                <div className="p-4 border-b border-[#1E293B] flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                  <div className="font-heading font-semibold text-sm">{s.name}</div>
                  <div className="ml-auto text-right">
                    <div className="text-xs text-slate-500">{list.length}</div>
                    <div className="text-[10px] text-[#0CDBF2] font-mono">{BRL(list.reduce((a,b)=>a+(b.value||0),0))}</div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {list.map((it) => {
                    const pi = printInfo(it);
                    return (
                    <div key={it.id} draggable onDragStart={() => setDragged(it)} onDragEnd={() => setDragged(null)}
                      onTouchStart={(e) => touchStart(e, it, it.title)}
                      onClick={() => it.opportunity_id && setDetailItem(it)}
                      data-testid={`prod-card-${it.id}`}
                      style={{ opacity: drag?.item?.id === it.id ? 0.4 : 1, touchAction: "pan-y" }}
                      className="bg-[#111622] border border-[#1E293B] hover:border-[#0CDBF2]/50 rounded-lg p-3 cursor-pointer active:cursor-grabbing transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-sm">{it.title}</div>
                        <button onClick={(e)=>{e.stopPropagation(); setPrintItem(it);}} title={pi.tip} data-testid={`prod-print-${it.id}`}
                          className={`shrink-0 transition-colors ${pi.isRed ? "text-red-400 hover:text-red-300" : "text-emerald-400 hover:text-emerald-300"}`}><Printer size={14}/></button>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">{it.customer_name}</div>
                      <div className="flex items-center justify-between mt-2 text-[11px]">
                        <span className="text-[#0CDBF2] flex items-center gap-1"><Package size={11}/>{totalPieces(it)} pçs</span>
                        <span className="text-emerald-400 font-mono">{BRL(it.value||0)}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-1"><Clock size={10}/>{fmt(it.closed_at)}</div>
                      {it.size_grid?.length > 0 && <div className="text-[10px] text-slate-500 mt-1 line-clamp-2" data-testid={`prod-grid-${it.id}`}>{gridSummary(it)}</div>}
                      <div className="flex items-center gap-2 mt-2" onClick={(e)=>e.stopPropagation()} onMouseDown={(e)=>e.stopPropagation()}>
                        <input type="date" value={it.due_date || ""} min={todayISO} onChange={(e)=>setDue(it.id, e.target.value)} data-testid={`prod-due-${it.id}`}
                          className={`bg-[#0B0F17] rounded px-1.5 py-0.5 text-[11px] text-slate-300 border ${(!it.due_date && it.stage !== "entregue") ? "border-red-500/70" : "border-slate-800"}`}/>
                        {dueInfo(it)
                          ? <span className={`text-[10px] px-1.5 py-0.5 rounded border ${dueInfo(it).cls}`}>{dueInfo(it).label}</span>
                          : (it.stage !== "entregue" && <span className="text-[10px] px-1.5 py-0.5 rounded border text-red-400 border-red-500/40" data-testid={`prod-due-required-${it.id}`}>Prazo obrigatório</span>)}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="px-6 pb-6">
          <div className="rounded-xl border border-[#1E293B] bg-[#111622] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0B0F17] text-slate-400">
                <tr>{["Pedido","Cliente","Peças","Valor","Fechado em","Prazo","Etapa","Imprimir"].map(h=><th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan="8" className="text-center py-12 text-slate-500">Nenhum item em produção.</td></tr>}
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-slate-800/70 cursor-pointer hover:bg-[#0B0F17]" onClick={() => it.opportunity_id && setDetailItem(it)} data-testid={`prod-row-${it.id}`}>
                    <td className="px-4 py-3 font-medium">{it.title}</td>
                    <td className="px-4 py-3 text-slate-300">{it.customer_name}</td>
                    <td className="px-4 py-3 text-[#0CDBF2] font-mono">{totalPieces(it)}</td>
                    <td className="px-4 py-3 text-emerald-400 font-mono">{BRL(it.value||0)}</td>
                    <td className="px-4 py-3 text-slate-400">{fmt(it.closed_at)}</td>
                    <td className="px-4 py-3" onClick={(e)=>e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <input type="date" value={it.due_date || ""} min={todayISO} onChange={(e)=>setDue(it.id, e.target.value)} data-testid={`prod-due-list-${it.id}`}
                          className={`bg-[#07090E] rounded px-1.5 py-0.5 text-xs text-slate-300 border ${(!it.due_date && it.stage !== "entregue") ? "border-red-500/70" : "border-slate-800"}`}/>
                        {dueInfo(it)
                          ? <span className={`text-[10px] px-1.5 py-0.5 rounded border ${dueInfo(it).cls}`}>{dueInfo(it).label}</span>
                          : (it.stage !== "entregue" && <span className="text-[10px] px-1.5 py-0.5 rounded border text-red-400 border-red-500/40">Prazo obrigatório</span>)}
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e)=>e.stopPropagation()}>
                      <select value={it.stage} onChange={(e)=>move(it, e.target.value)} data-testid={`prod-stage-${it.id}`}
                        className="bg-[#07090E] border border-slate-800 rounded-md h-8 px-2 text-xs">
                        {stages.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3" onClick={(e)=>e.stopPropagation()}>
                      {(() => { const pi = printInfo(it); return (
                        <button onClick={()=>setPrintItem(it)} title={pi.tip} data-testid={`prod-print-list-${it.id}`}
                          className={`${pi.isRed ? "text-red-400 hover:text-red-300" : "text-emerald-400 hover:text-emerald-300"}`}><Printer size={16}/></button>
                      ); })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detailItem && (
        <OpportunityDetail oppId={detailItem.opportunity_id} stages={pipelineStages}
          productionItem={detailItem} productionStages={stages}
          onClose={() => setDetailItem(null)} onUpdated={load}
          onRequestPrint={() => setPrintItem(detailItem)} />
      )}
      {printItem && (
        <PrintOptionsModal item={printItem}
          onClose={() => setPrintItem(null)}
          onPrinted={load} />
      )}
      <DragGhost drag={drag} />
    </div>
  );
}
